import React, { useCallback, useMemo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { RefreshCw } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { ExecutionModalSkeleton } from './components/DashboardReview/ExecutionSkeleton'
import { BaseModalV3, Button, CloseButton } from 'components/ui'
import { AppDispatch } from 'config/redux'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { loadTaskOverviewModal, patchTaskOverviewModal, setTaskOverviewModal } from 'redux/actions/tasks_v2'
import { DashboardReviewModal } from './components/DashboardReview'
import { TaskOverviewModal } from './TaskOverviewModal'
import { ITaskOverviewContentStack, TaskOverviewContentType } from './TaskOverviewStackProvider'

const TaskModalError: React.FC<{ onClose: () => void; onRetry: () => void }> = ({ onClose, onRetry }) => (
  <BaseModalV3
    floating
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" h="60px">
        <Text fontSize="16px" fontWeight={700} color="#0F172A">
          Task
        </Text>
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Flex h="100%" minH="320px" alignItems="center" justifyContent="center" flexDirection="column" textAlign="center">
        <Text fontSize="15px" fontWeight={700} color="#0F172A">
          Unable to load task
        </Text>
        <Text fontSize="13px" color="#64748B" mt="8px" maxW="320px">
          Something went wrong while loading this task context.
        </Text>
        <Button variant="secondary" mt="18px" onClick={onRetry} startIcon={<RefreshCw size={15} />}>
          Retry
        </Button>
      </Flex>
    }
    footer={<Box />}
    bodyProps={{ maxHeight: '70vh' }}
  />
)

export const TaskOverviewModalHost: React.FC = () => {
  const dispatch: AppDispatch = useDispatch()
  const modal = useSelector((state: RootState) => state.tasks_v2.taskOverviewModal)
  const task = useSelector((state: RootState) =>
    state.tasks_v2.taskFromInsights?.tid === modal?.tid ? state.tasks_v2.taskFromInsights : null
  )
  const subtasks = useSelector((state: RootState) => (modal ? state.tasks_v2.subtasks[modal.tid] ?? [] : []))

  const initialContent = useMemo<ITaskOverviewContentStack | undefined>(() => {
    if (!modal?.targetSubtaskId) return undefined

    const selectedSubtask = subtasks.find((subtask) => subtask.st_id === modal.targetSubtaskId)
    if (!selectedSubtask) return undefined

    const baseProps = {
      selectedSubtask,
      targetParentCommentId: modal.targetParentCommentId,
      targetCommentId: modal.targetCommentId,
      targetMediaIndex: modal.targetMediaIndex,
    }

    if (
      selectedSubtask.type === keys.SUBTASK_TYPES.POLL_SUBTASK ||
      selectedSubtask.type === keys.SUBTASK_TYPES.PRIVATE_POLL
    ) {
      return {
        contentType: TaskOverviewContentType.VOTES_OVERVIEW,
        props: { ...baseProps, title: selectedSubtask.title },
      }
    }

    if (selectedSubtask.type === keys.SUBTASK_TYPES.OPEN_QUESTION) {
      return {
        contentType: TaskOverviewContentType.OPEN_QUESTION_OVERVIEW,
        props: { ...baseProps, title: selectedSubtask.title },
      }
    }

    return {
      contentType: TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING,
      props: baseProps,
    }
  }, [modal, subtasks])

  const handleClose = useCallback(() => {
    dispatch(setTaskOverviewModal(null))
  }, [dispatch])

  const handleRetry = useCallback(async () => {
    if (!modal) return
    dispatch(patchTaskOverviewModal({ isLoading: true, error: null }))
    await dispatch(loadTaskOverviewModal())
  }, [dispatch, modal])

  if (!modal) return null
  if (modal.isLoading) {
    return <ExecutionModalSkeleton onClose={handleClose} media={!!modal.targetSubtaskId} />
  }
  if (modal.error || !task) return <TaskModalError onClose={handleClose} onRetry={handleRetry} />

  if (modal.reviewContexts?.length)
    return (
      <DashboardReviewModal
        key={`${modal.tid}:${modal.result_context_id}:${modal.recurrence_ts || ''}`}
        task={task}
        contexts={modal.reviewContexts}
        onClose={handleClose}
      />
    )

  return <TaskOverviewModal onClose={handleClose} task={task} isComeFromDashboard content={initialContent} />
}
